import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Page from '../../components/Page';
import {
  fetchAccommodations,
  getDomain,
  getColorVars,
  fetchConfig,
} from '../../utils/functions';
import Spinner from '../../components/Spinner';
import { QuotaInfo } from '../../types/accommodation';
import houseIcon from './house.svg';
import QuotaTable from './QuotaTable';
import { Config } from '../../types/drupal';

interface Props {
  idToken: string;
  email: string;
}

const QuotasPage: React.FC<Props> = (props: Props): JSX.Element => {
  const [isLoading, setIsLoading] = useState(true);
  const [apartments, setApartments] = useState<QuotaInfo[]>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedApartment, setSelectedApartment] = useState<QuotaInfo>();

  const [config, setConfig] = useState<Config | undefined>();

  const { t, i18n } = useTranslation('quotas');
  const { bgColor } = getColorVars();

  const domain = getDomain();
  const pageId: { [key: string]: string } = {};
  pageId['ruka'] = process.env.REACT_APP_QUOTAS_PAGE_RUKA ?? '';
  pageId['pyha'] = process.env.REACT_APP_QUOTAS_PAGE_PYHA ?? '';

  // Fetch config that contains texts for quotas.
  useEffect(() => {
    async function getConfig() {
      const data = await fetchConfig(i18n.language, props.idToken);
      if (data) {
        setConfig(data);
      }
    }
    if (props.idToken) {
      getConfig();
    }
  }, [i18n.language, props.idToken]);

  useEffect(() => {
    async function getAccommodations() {
      setIsLoading(true);
      // reservation type is only needed for one request,
      // since ownershipdata and quotas are returned the same
      // regardless of resort.
      const rukaData = await fetchAccommodations({ token: props.idToken, reservationType: 1, resort: 'Ruka'});
      const pyhaData = await fetchAccommodations({ token: props.idToken, reservationType: 1, resort: 'Pyha'});

      const quotasForApartment: QuotaInfo[] = [];
      if (
        rukaData &&
        pyhaData &&
        rukaData.quotas &&
        rukaData.ownershipData &&
        rukaData.ownershipData.rooms
      ) {
        const { quotas, ownershipData } = rukaData;
        const result = [...rukaData.result, ...pyhaData.result];

        Object.keys(quotas).forEach((quota: string) => {
          const apartment = result.find(
            (result) => parseInt(result.roomTypeId) === parseInt(quota)
          );

          if (apartment && apartment !== undefined) {
            const title = apartment.field_title[i18n.language];

            let quotasSorted = quotas[quota].sort((a, b) =>
              new Date(a.end).getTime() > new Date(b.end).getTime() ? 1 : -1
            );

            const date = new Date();
            quotasSorted = quotasSorted
              // Filter out quotas if the season has passed.
              .filter((quota) => {
                if (
                  quota.end &&
                  new Date(quota.end) < date
                ) {
                  return false;
                }
                return true;
              });
        
            const roomIds = ownershipData.rooms
              .map((room) => {
                if (
                  parseInt(room.roomTypeId) === parseInt(apartment.roomTypeId)
                ) {
                  return room.roomId;
                } else {
                  // Return something so the type is correct, empty strings
                  // get filtered out anyways.
                  return '';
                }
              })
              .filter((roomId) => roomId !== '');

            const apartmentInfo: QuotaInfo = {
              title,
              quotas: quotasSorted,
              roomTypeId: quota,
              roomIds,
            };

            quotasForApartment.push(apartmentInfo);
          }
        });

        setApartments(quotasForApartment);
        setSelectedApartment(quotasForApartment[0]);
      } else {
        setErrorMessage(t('errorMessage'));
      }

      if (quotasForApartment.length === 0) {
        setErrorMessage(t('errorMessage'));
      }
      setIsLoading(false);
    }

    if (props.idToken) {
      getAccommodations();
    }
  }, [props.idToken, i18n.language]);

  function onApartmentSelect(event: React.ChangeEvent<HTMLSelectElement>) {
    const newApartment = apartments?.find(
      (apartment) => apartment.roomTypeId === event.target.value
    );
    setSelectedApartment(newApartment);
  }

  let mainContent = <Spinner />;

  const quotaSelector = (
    <div
      className={`${bgColor} w-full ml-auto flex flex-col justify-center py-8 px-6`}
    >
      <div className='flex items-center mb-4'>
        <img
          src={houseIcon}
          alt={t('Quota selector icon') ?? 'Quota selector icon'}
          className='h-6 w-auto mr-4'
        />{' '}
        <p className='text-white'>{t('Pick an apartment')}</p>
      </div>

      <select
        id='quotaselector'
        name='quotaselector'
        onChange={onApartmentSelect}
        disabled={isLoading ? true : false}
        className={`rounded h-12 px-4 relative ${
          isLoading ? 'disabled:opacity-75' : ''
        }`}
      >
        {isLoading && <option value='Loading'>{t('Loading')}</option>}
        {apartments &&
          apartments.map((quota, i) => {
            return (
              <option value={quota.roomTypeId} key={i}>
                {quota.title}
              </option>
            );
          })}
      </select>
    </div>
  );

  if (selectedApartment?.quotas[0]) {
    mainContent = (
      <QuotaTable
        info={selectedApartment?.quotas[0]}
        email={props.email}
        language={i18n.language}
        config={config ?? null}
        quotaInfo={selectedApartment}
        idToken={props.idToken}
      />
    );
  }

  const secondaryContent = (
    <>
      {selectedApartment?.quotas.slice(1).map((quota, i) => {
        return (
          <QuotaTable
            key={i}
            index={i}
            info={quota}
            email={props.email}
            language={i18n.language}
            config={config ?? null}
            quotaInfo={selectedApartment}
            idToken={props.idToken}
          />
        );
      })}
    </>
  );

  if (errorMessage) {
    mainContent = <p>{errorMessage}</p>;
  }

  return (
    <Page
      pageId={pageId[domain] ?? ''}
      mainContent={mainContent}
      topContent={quotaSelector}
      secondaryContent={secondaryContent}
    ></Page>
  );
};

export default QuotasPage;
